import { Link } from "react-router-dom";
import { ArrowRight, Stethoscope, Sparkles, HeartPulse, ShieldCheck, Globe2 } from "lucide-react";
import { SEO } from "@/components/SEO";
import { PAGE_SEO } from "@/lib/seoData";
import { SectionHeading, Reveal } from "@/components/Section";

const APPLICATIONS = [
  {
    icon: Stethoscope,
    eyebrow: "01 · Clinical Opportunity",
    title: "Medical Aesthetics & Dermatology",
    body: "A proposed bridge between personalized post-treatment recommendations and whether patients actually keep using them. Clinical value remains to be validated.",
    to: "/clinical",
    cta: "Explore the Clinical Opportunity",
  },
  {
    icon: Sparkles,
    eyebrow: "02",
    title: "Consumer Skincare",
    body: "Sunscreen, moisturizer and body spray, kept wearable, accessible and ready the moment daily routines call for them.",
    to: "/why",
    cta: "See Why WearSpray",
  },
  {
    icon: HeartPulse,
    eyebrow: "03",
    title: "Healthcare & Sanitization",
    body: "Hand hygiene that moves with the caregiver, closing the distance between a hygiene opportunity and the ability to act.",
    to: "/global-health",
    cta: "See Healthcare Applications",
  },
  {
    icon: ShieldCheck,
    eyebrow: "04",
    title: "Personal Protection",
    body: "Sunscreen, repellent and sanitizer, carried on the wrist instead of left behind in a bag or forgotten at home.",
    to: "/products",
    cta: "See the Product",
  },
  {
    icon: Globe2,
    eyebrow: "05",
    title: "Global Health",
    body: "A refillable, wearable repellent dispenser designed to reach vulnerable communities during the daylight hours bed nets can't cover.",
    to: "/global-health",
    cta: "See the Global Health Initiative",
  },
];

export default function Applications() {
  return (
    <div className="min-h-screen bg-black text-white overflow-x-hidden" data-testid="applications-page">
      <SEO {...PAGE_SEO["/applications"]} path="/applications" />

      {/* HERO */}
      <section className="relative border-b border-zinc-800 overflow-hidden" data-testid="applications-hero">
        <div className="absolute inset-0 z-0">
          <img
            loading="eager"
            src="https://images.pexels.com/photos/6129494/pexels-photo-6129494.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600"
            alt="Diverse healthcare and skincare professionals"
            className="absolute inset-0 w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/85 to-black" />
        </div>
        <div className="relative z-10 max-w-5xl mx-auto px-6 md:px-12 pt-28 pb-16 md:pt-36 md:pb-20">
          <Reveal>
            <span className="font-mono text-xs tracking-[0.28em] uppercase text-accent block mb-7">
              Applications
            </span>
            <h1 className="font-heading font-light tracking-tight leading-[1.1] text-4xl sm:text-5xl lg:text-6xl max-w-3xl">
              One platform. Five opportunities.
            </h1>
            <p className="font-body text-zinc-400 text-lg md:text-xl leading-relaxed mt-7 max-w-2xl">
              A single patented dispensing architecture, applied across skincare, medical
              aesthetics, healthcare, personal protection and global health. Some are already in
              market, others are early opportunities we're exploring with partners.
            </p>
          </Reveal>
        </div>
      </section>

      {/* FIVE OPPORTUNITIES */}
      <section className="max-w-6xl mx-auto px-6 md:px-12 py-20 md:py-28" data-testid="applications-list-section">
        <div className="flex flex-col gap-6">
          {APPLICATIONS.map((a, i) => (
            <Reveal key={a.title} delay={i * 0.06}>
              <Link
                to={a.to}
                data-testid={`applications-card-${i}`}
                className="group grid grid-cols-1 md:grid-cols-[auto_1fr_auto] items-center gap-6 border border-zinc-800 bg-[#0d0d0d] hover:border-accent/40 rounded-xl p-7 md:p-8 transition-colors"
              >
                <div className="w-12 h-12 shrink-0 rounded-lg border border-accent/40 text-accent flex items-center justify-center">
                  <a.icon size={22} strokeWidth={1.6} />
                </div>
                <div>
                  <span className="font-mono text-xs tracking-[0.2em] uppercase text-accent">{a.eyebrow}</span>
                  <h3 className="font-heading text-xl md:text-2xl font-medium text-white mt-2">{a.title}</h3>
                  <p className="font-body text-sm text-zinc-400 leading-relaxed mt-3 max-w-2xl">{a.body}</p>
                </div>
                <span className="inline-flex items-center gap-2 font-body text-sm font-medium text-white shrink-0 group-hover:gap-3 transition-all">
                  {a.cta}
                  <ArrowRight size={15} />
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-zinc-900 bg-[#0a0a0a]" data-testid="applications-cta-section">
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-20 md:py-28 text-center">
          <Reveal>
            <h2 className="font-heading text-3xl md:text-5xl font-light tracking-tight leading-tight max-w-3xl mx-auto">
              See an application that isn't listed yet?
            </h2>
            <p className="font-body text-zinc-400 text-lg leading-relaxed mt-5 max-w-xl mx-auto">
              We're actively exploring new partnerships across personal care, medical and public health markets.
            </p>
            <Link
              to="/contact"
              data-testid="applications-cta-link"
              className="inline-flex items-center gap-2 font-body text-sm font-medium bg-[#00e5ff] text-black px-8 py-4 mt-9 hover:bg-[#00b3cc] transition-colors"
            >
              Partner With Us
              <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
